/**
 * What a Compare step's editor offers (AMO-301): the earlier steps that pass
 * on tables, and the words for each rule's check.
 *
 * @module components/workflows/CompareParts.logic
 */
import type { WorkflowCompareCheck, WorkflowGraph, WorkflowNode } from "@amoreg/contracts";
import { workflowAncestors } from "@repo/shared/workflowGraph";

/** Steps that can pass on a table: Intake (spreadsheets it reads), Ingestion read as rows, Compare, and For each row. */
const passesTables = (node: WorkflowNode): boolean =>
  node.type === "intake" ||
  node.type === "compare" ||
  node.type === "for-each" ||
  (node.type === "ingestion" && node.config.shape === "rows");

/** The steps before `nodeId` whose tables a Compare step can check, in run order. */
export function compareTableSources(graph: WorkflowGraph, nodeId: string): ReadonlyArray<WorkflowNode> {
  const byId = new Map(graph.nodes.map((node) => [node.id, node]));
  return workflowAncestors(graph, nodeId)
    .map((id) => byId.get(id))
    .filter((node): node is WorkflowNode => node !== undefined && passesTables(node));
}

export const COMPARE_CHECKS: ReadonlyArray<{ readonly value: WorkflowCompareCheck; readonly label: string; readonly limit: string | null }> = [
  { value: "percent", label: "Differs by more than %", limit: "%" },
  { value: "absolute", label: "Differs by more than", limit: "amount" },
  { value: "equal", label: "Must match", limit: null },
  { value: "unchanged", label: "Must change (flag unchanged)", limit: null },
];

/** The words for a rule's check. */
export const compareCheckLabel = (check: WorkflowCompareCheck): string =>
  COMPARE_CHECKS.find((choice) => choice.value === check)?.label ?? check;
